import Raw from 'components/raw/raw';
import { OrderBy, ProReviewBoolExp, useReviewsQuery } from 'generated/graphql';
import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';
import { useProReviewsCount } from 'hooks/use-pro-reviews-count/use-pro-reviews-count';
import { isProofStatus } from 'modules/review/types/proof.type';
import React, { FunctionComponent, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Container,
  Icon,
  Label,
  Loader,
  Message,
  Pagination,
  Popup,
  Segment,
} from 'semantic-ui-react';
import { formatDate } from 'utils';
import { getIndividualTimelinePath } from '../utils/getIndividualTimelinePath';
import {
  getReviewReplyStatus,
  reviewReplyStatusLabels,
} from '../utils/getReviewReplyStatus';
import { CopyReviewLinkButton } from './CopyReviewLinkButton';
import { EditReviewButton } from './EditReviewButton';
import { ProofDocumentButton } from './ProofDocumentButton';
import { ReviewContent } from './ReviewContent';
import { ReviewHeader } from './ReviewHeader';
import { ReviewMetaLine } from './ReviewMetaLine';
import { ReviewSourceLabel } from './ReviewSourceLabel';
import { ReviewStatusLabel } from './ReviewStatusLabel';
import { ReviewTrustLevelBadge } from './ReviewTrustLevelBadge';
import { ViewReviewOnlineButton } from './ViewReviewOnlineButton';
import { hasOnlyArchivedViews } from '../utils/hasOnlyArchivedViews';
import { ReviewPresentationsButton } from './ReviewPresentationsButton';
import { ReviewPresentationsLine } from './ReviewPresentationsLine';

const ITEMS_PER_PAGE = 10;

type ReviewListProps = {
  presentationId: number;
  proViewId?: number;
};

export const ReviewList: FunctionComponent<ReviewListProps> = ({
  presentationId,
  proViewId,
}) => {
  const navigate = useNavigate();
  const { currentBilikPerson } = useCurrentBilikPerson();
  const [page, setPage] = useState(1);

  const where: ProReviewBoolExp = useMemo(
    () => ({
      isSpam: { _eq: false },
      presentations: { presentationId: { _eq: presentationId } },
    }),
    [presentationId],
  );

  const { data, loading, refetch } = useReviewsQuery({
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (page - 1) * ITEMS_PER_PAGE,
      order_by: [{ dateCreated: OrderBy.DescNullsLast }],
      where,
    },
  });

  const { count } = useProReviewsCount(where);

  const reviews = data?.proReview;

  if (loading && !reviews) {
    return (
      <Loader
        style={{ marginTop: '50px' }}
        size="large"
        active
        inline="centered"
      >
        Chargement...
      </Loader>
    );
  }

  if (!reviews || reviews.length === 0) {
    return <Message>Aucun avis pour ce professionnel.</Message>;
  }

  return (
    <>
      {reviews.map((review) => {
        const isLockedOrArchived =
          (!!review.locker && review.locker.id !== currentBilikPerson?.id) ||
          hasOnlyArchivedViews(review);
        const replyStatus = getReviewReplyStatus(review);

        return (
          <Segment.Group key={review.id} style={{ marginBottom: '24px' }}>
            <ReviewHeader
              title={`${review.givenName} ${review.familyName}`}
              labels={
                <>
                  <ReviewStatusLabel review={review} />
                  <ReviewSourceLabel source={review.source} />
                  {review.status === 'ok' ? (
                    <ReviewTrustLevelBadge trustLevel={review.trustLevel} />
                  ) : null}
                </>
              }
              meta={<ReviewMetaLine review={review} />}
            />
            <Segment>
              <ReviewContent review={review} title={review.reviewAspect} />
              {review.reply ? (
                <p>
                  <b>
                    Réponse du pro
                    {review.replyDate
                      ? ` · ${formatDate(review.replyDate)}`
                      : ''}
                    {replyStatus !== 'none' && replyStatus !== 'validated' ? (
                      <Label
                        as="span"
                        size="mini"
                        color={reviewReplyStatusLabels[replyStatus].color}
                        style={{ marginLeft: '6px' }}
                      >
                        {reviewReplyStatusLabels[replyStatus].text}
                      </Label>
                    ) : null}
                  </b>
                  <br />
                  <Raw>{review.reply}</Raw>
                </p>
              ) : null}
            </Segment>
            <ReviewPresentationsLine
              review={review}
              onReviewChange={refetch}
              presentationId={presentationId}
            />
            <Segment>
              <Popup
                content="Ouvrir l'avis dans la page de modération"
                position="top center"
                trigger={
                  <Button
                    basic
                    primary
                    onClick={(): void => {
                      navigate(`/reviews?id=${review.id}&allZones=1`);
                    }}
                  >
                    <Icon name="external alternate" />
                    Modérer
                  </Button>
                }
              />
              <EditReviewButton
                reviewId={review.id}
                disabled={isLockedOrArchived}
              />
              {review.status !== 'ko' && review.source === 'BILIK' ? (
                <ProofDocumentButton
                  reviewId={review.id}
                  proofStatus={
                    isProofStatus(review.proofStatus)
                      ? review.proofStatus
                      : undefined
                  }
                  onProofChange={refetch}
                  disabled={isLockedOrArchived}
                />
              ) : null}
              {review.status === 'ok' ? (
                <>
                  <ViewReviewOnlineButton
                    review={review}
                    proViewId={proViewId}
                  />
                  <CopyReviewLinkButton review={review} proViewId={proViewId} />
                </>
              ) : null}
              <ReviewPresentationsButton
                review={review}
                onReviewChange={refetch}
              />
              <Popup
                content="Timeline du particulier"
                position="top center"
                trigger={
                  <Button
                    as={Link}
                    to={getIndividualTimelinePath(review)}
                    basic
                    color="grey"
                    icon
                  >
                    <Icon name="time" />
                  </Button>
                }
              />
            </Segment>
          </Segment.Group>
        );
      })}
      {(count ?? 0) > ITEMS_PER_PAGE ? (
        <Container fluid textAlign="right" style={{ marginTop: '15px' }}>
          <Pagination
            activePage={page}
            totalPages={Math.ceil((count ?? 0) / ITEMS_PER_PAGE)}
            onPageChange={(_, paginationData): void => {
              setPage(Number(paginationData.activePage));
            }}
            ellipsisItem={{
              content: <Icon name="ellipsis horizontal" />,
              icon: true,
            }}
            firstItem={{
              content: <Icon name="angle double left" />,
              icon: true,
            }}
            lastItem={{
              content: <Icon name="angle double right" />,
              icon: true,
            }}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
          />
        </Container>
      ) : null}
    </>
  );
};
